import { AsyncActionCreators, Failure, Success } from 'typescript-fsa';
import { ReducerBuilder } from 'typescript-fsa-reducers';

import { ApiError } from '../ApiError';
import { getNetworkStatusStarted, getNetworkStatusDone, getNetworkStatusFailed } from '../networkStatus';

export const applyAsyncReducer = <TData, TMeta>() => {
	return <
		TStore,
		TFetchPayload,
		TResultPayload,
		TErrorPayload extends ApiError,
		TAction extends AsyncActionCreators<TFetchPayload, TResultPayload, TErrorPayload>,
		TProp extends keyof TStore,
		TNetworkStatusPropName extends keyof TMeta,
	>
	(
		propName: TProp,
		networkStatusPropName: TNetworkStatusPropName,
		action: TAction,
		builder: ReducerBuilder<TStore>,
		prepareResult: (params: { store: TStore & { [key: string]: { meta: TMeta; data: TData } }; result: TResultPayload; params: TFetchPayload }) => Partial<TData> = null,
	) =>
		builder
			.case(
				action.started,
				(store: TStore & { [key: string]: { meta: TMeta } }, payload) => ({
					...store,
					[propName]: {
						...store[propName],
						meta: {
							...store[propName].meta,
							[networkStatusPropName]: getNetworkStatusStarted(),
						},
					},
				}),
			)
			.case(
				action.failed,
				(
					store: TStore & { [key: string]: { meta: TMeta } },
					{ error }: Failure<TFetchPayload, TErrorPayload>,
				) => ({
					...store,
					[propName]: {
						...store[propName],
						meta: {
							...store[propName].meta,
							[networkStatusPropName]: getNetworkStatusFailed(error),
						},
					},
				}),
			)
			.case(
				action.done,
				(store: TStore & { [key: string]: { meta: TMeta; data: TData } }, payload: Success<TFetchPayload, TResultPayload>) => {
					let data: Partial<TData>;

					if (prepareResult) {
						const result = prepareResult({ store, result: payload.result, params: payload.params });

						if (result === null) {
							data = result;
						} else if (Array.isArray(result)) {
							data = result;
						} else {
							data = {
								...store[propName].data,
								...result,
							};
						}
					} else {
						data = store[propName].data;
					}

					return {
						...store,
						[propName]: {
							...store[propName],
							data,
							meta: {
								...store[propName].meta,
								[networkStatusPropName]: getNetworkStatusDone(),
							},
						},
					};
				},
			);
};
